<template>
  <div @dragover="onRootOver" @drop="onRootDrop">
    <CCol>
      <div class="form-group">
        <label>{{ label }}</label>
        <div class="is-wrap" :class="{'is-dragover': field_drag, 'is-busy': field_uploading}"
             @dragenter.prevent.stop="onFieldEnter" @dragover.prevent.stop="onFieldEnter"
             @dragleave="field_drag = false" @drop.prevent.stop="onFieldDrop">
          <CImg height="100px" width="100px" class="border-dark is-preview" :src="preview" @click.native="open_library"/>
          <div class="is-drophint" v-if="field_drag || field_uploading">
            {{ field_uploading ? 'در حال آپلود... ' + field_progress + '%' : 'رها کنید تا آپلود و انتخاب شود' }}
          </div>
          <div class="is-gallery" v-if="multiple">
            <div v-for="(id, index) in selected_media" :key="id" class="is-thumb">
              <span class="is-remove" title="حذف از گالری" @click="remove(index)">✕</span>
              <span class="is-main" v-if="index === 0">اصلی</span>
              <img :src="url_of(id)" @click="preview = url_of(id)" alt=""/>
            </div>
          </div>
        </div>
      </div>
    </CCol>

    <CModal title="کتابخانه رسانه" color="dark" size="xl" :show.sync="file_manager_modal" :close-on-backdrop="false">
      <MediaLibraryBrowser v-if="file_manager_modal" ref="browser"
                           picker
                           :multiple="multiple"
                           initial-type="image"
                           :value="selected_media"
                           @input="on_select_ids"
                           @items="on_select_items"
                           @pick="choose"/>
      <template #footer>
        <span class="text-muted ml-auto is-footer-note" v-if="multiple">اولین تصویر انتخاب‌شده، تصویر اصلی است.</span>
        <CButton color="dark" variant="outline" @click="file_manager_modal = false">بستن</CButton>
        <CButton color="primary" @click="choose">انتخاب</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { apiUrl } from '@/api/config'
import axios from "@/api/http";
import MediaLibraryBrowser from "@/views/media/MediaLibraryBrowser.vue";

// Media picker: opens the media library. API kept compatible with the old file manager:
//  :media_id.sync = [ids], :previewImage.sync = url, multiple, label, default_archive (ignored).
export default {
  name: 'ImageSelector',
  inheritAttrs: false,
  components: {MediaLibraryBrowser},
  props: {
    baseUrl: String,
    label: String,
    multiple: {type: [Boolean], default: false},
    media_id: Array,
    default_archive: String,
    base64: String,
    previewImage: {type: [String], default: "/img/placeholder.png"}
  },
  data() {
    return {
      file_manager_modal: false,
      selected_media: [],
      urls: {},
      preview: "/img/placeholder.png",
      field_drag: false,
      field_uploading: false,
      field_progress: 0,
    }
  },
  watch: {
    previewImage() {
      this.set_preview(this.previewImage);
    },
    media_id() {
      this.selected_media = this.media_id || [];
      this.fetch_missing();
    },
  },
  mounted() {
    this.set_preview(this.previewImage);
    if (this.media_id != null) {
      this.selected_media = this.media_id;
      this.fetch_missing();
    }
  },
  methods: {
    set_preview(v) {
      if (!v) { this.preview = "/img/placeholder.png"; return; }
      if (/^(https?:)?\/\//.test(v) || v.startsWith('/img/') || v.startsWith('data:')) this.preview = v;
      else this.preview = (this.baseUrl || apiUrl('')) + String(v).replace(/^\/+/, '');
    },
    url_of(id) {
      return this.urls[id] || "/img/placeholder.png";
    },
    fetch_missing() {
      const missing = (this.selected_media || []).filter((id) => id && !this.urls[id]);
      if (!missing.length) return;
      const fd = new FormData();
      missing.forEach((id) => fd.append('ids[]', id));
      axios.post('/api/admin/media/get', fd, {show_pros: false}).then((r) => {
        ((r.data && r.data.items) || []).forEach((it) => this.$set(this.urls, it.id, it.url));
        if (this.multiple && this.selected_media.length && (!this.previewImage || this.previewImage === '/img/placeholder.png')) {
          this.preview = this.url_of(this.selected_media[0]);
        }
      }).catch(() => {});
    },
    open_library() {
      this.file_manager_modal = true;
    },
    on_select_items(items) {
      items.forEach((it) => this.$set(this.urls, it.id, it.url));
    },
    on_select_ids(ids) {
      // mutate in place so parents holding the same array (gallery) see the change, like the old picker
      if (Array.isArray(this.media_id)) {
        this.media_id.splice(0, this.media_id.length, ...ids);
        this.selected_media = this.media_id;
      } else {
        this.selected_media = ids.slice();
      }
      const first = this.multiple ? ids[ids.length - 1] : ids[0];
      if (first && this.urls[first]) this.preview = this.urls[first];
      this.$emit('update:media_id', this.selected_media);
      this.$emit('update:previewImage', this.preview);
    },
    remove(index) {
      this.selected_media.splice(index, 1);
      this.$emit('update:media_id', this.selected_media);
    },
    choose() {
      this.file_manager_modal = false;
    },
    has_files(ev) {
      return !!(ev.dataTransfer && Array.from(ev.dataTransfer.types || []).indexOf('Files') >= 0);
    },
    // Drops anywhere in the picker modal (header/footer too) go to the library's uploader;
    // also stops the browser from opening a file dropped on this component.
    onRootOver(ev) {
      if (this.has_files(ev)) ev.preventDefault();
    },
    onRootDrop(ev) {
      if (!this.has_files(ev)) return;
      ev.preventDefault();
      if (ev.target && ev.target.closest && ev.target.closest('.mlb')) return; // handled by the library
      const files = Array.from(ev.dataTransfer.files || []);
      if (this.file_manager_modal && this.$refs.browser && files.length) this.$refs.browser.upload_files(files);
    },
    onFieldEnter(ev) {
      if (this.has_files(ev)) this.field_drag = true;
    },
    // Drop straight onto the field: upload to the library (server routes it to the type's root folder) and select it.
    onFieldDrop(ev) {
      this.field_drag = false;
      let files = Array.from((ev.dataTransfer && ev.dataTransfer.files) || []);
      if (!files.length || this.field_uploading) return;
      if (!this.multiple) files = files.slice(0, 1);
      this.field_uploading = true;
      this.field_progress = 0;
      const ids = this.multiple ? (this.selected_media || []).slice() : [];
      let done = 0;
      const next = (i) => {
        if (i >= files.length) return Promise.resolve();
        const fd = new FormData();
        fd.append('files', files[i], files[i].name);
        return axios.post('/api/admin/media/upload', fd, {
          show_pros: false,
          timeout: 0,
          onUploadProgress: (e) => {
            if (e.total) this.field_progress = Math.min(99, Math.round(((done + e.loaded / e.total) * 100) / files.length));
          },
        }).then((r) => {
          const d = r.data || {};
          if (d.error || !d.items || !d.items.length) throw new Error(d.msg || 'خطا در آپلود');
          const item = d.items[0];
          this.$set(this.urls, item.id, item.url);
          if (ids.indexOf(item.id) < 0) ids.push(item.id);
          done++;
        }).catch((e) => {
          const d = e && e.response && e.response.data;
          if (this.$toast) this.$toast.open({message: (d && d.msg) || (e && e.message) || 'خطا در آپلود', type: 'error'});
        }).then(() => next(i + 1));
      };
      next(0).then(() => {
        this.field_uploading = false;
        if (done) {
          this.on_select_ids(this.multiple ? ids : [ids[ids.length - 1]]);
          if (this.$toast) this.$toast.open({message: done + ' فایل آپلود و انتخاب شد', type: 'success'});
        }
      });
    },
  },
}
</script>
<style scoped>
.is-wrap { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-start; }
.is-preview { cursor: pointer; object-fit: contain; border: 1px solid var(--border); border-radius: 8px; background: var(--surface-2); }
.is-gallery { display: flex; flex-wrap: wrap; gap: 6px; width: 100%; }
.is-thumb { position: relative; width: 68px; height: 68px; border: 1px solid var(--border); border-radius: 6px; overflow: hidden; background: var(--surface-2); }
.is-thumb img { width: 100%; height: 100%; object-fit: contain; background: #f4f5f7; object-position: center; cursor: pointer; }
.is-remove { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: var(--surface); color: #dc2626; font-size: 11px; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 0 0 1px var(--border); }
.is-main { position: absolute; bottom: 0; right: 0; left: 0; background: #111; color: #fff; font-size: 10px; text-align: center; }
.is-footer-note { font-size: 12px; }
.is-wrap { position: relative; border-radius: 10px; transition: box-shadow .15s, background .15s; }
.is-wrap.is-dragover { box-shadow: 0 0 0 2px var(--accent); background: var(--accent-soft); }
.is-wrap.is-busy { opacity: .85; }
.is-preview:hover { border-color: var(--accent); }
.is-drophint { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; padding: 6px;
  border: 2px dashed var(--accent); border-radius: 10px; background: var(--overlay-bg); color: var(--accent-text); font-size: 12px; font-weight: 600; pointer-events: none; z-index: 2; }
</style>
